import { takeLatest, call, put } from 'redux-saga/effects';
import {
  loadExpenses,
  loadExpensesSuccess,
  loadExpensesError,
  saveExpenseRequest,
  updateExpenseRequest,
  cancelExpenseRequest,
} from './actions';
import {
  listExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from '@/services/expenses';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateExpenses } from '@/containers/posData/actions';
import type { Expense } from '@/lib/types';

function mapExpense(dto: any): Expense {
  return {
    id: String(dto.id),
    category: dto.category ?? null,
    description: dto.description ?? '',
    amount: Number(dto.amount),
    expenseDate: dto.expenseDate ?? null,
    paymentMethod: dto.paymentMethod ?? null,
    createdByUserId: dto.createdByUserId ?? null,
    paidAmount: dto.paidAmount != null ? Number(dto.paidAmount) : undefined,
    status: (dto.status as Expense['status']) || 'completed',
    createdAt: dto.createdAt,
  };
}

export function* handleLoadExpenses(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadExpensesError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listExpenses, shopId);
    const expenses = raw.map(mapExpense);
    yield put(hydrateExpenses(expenses));
    yield put(loadExpensesSuccess(expenses));
  } catch (e: any) {
    yield put(loadExpensesError(e?.message || 'Failed to load expenses'));
  }
}

export function* handleSaveExpense(
  action: ReturnType<typeof saveExpenseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { category, description, amount, expenseDate, paymentMethod, paidAmount } = action.payload.expense;
    yield call(createExpense, shopId, { category, description, amount, expenseDate, paymentMethod, paidAmount });
    yield put(loadExpenses());
  } catch (e: any) {
    yield put(loadExpensesError(e?.message || 'Failed to save expense'));
  }
}

export function* handleUpdateExpense(
  action: ReturnType<typeof updateExpenseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, expense } = action.payload;
    yield call(updateExpense, shopId, Number(id), expense);
    yield put(loadExpenses());
  } catch (e: any) {
    yield put(loadExpensesError(e?.message || 'Failed to update expense'));
  }
}

export function* handleCancelExpense(
  action: ReturnType<typeof cancelExpenseRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deleteExpense, shopId, Number(action.payload.id));
    yield put(loadExpenses());
  } catch (e: any) {
    yield put(loadExpensesError(e?.message || 'Failed to cancel expense'));
  }
}

export default function* saga() {
  yield takeLatest(loadExpenses.type, handleLoadExpenses);
  yield takeLatest(saveExpenseRequest.type, handleSaveExpense);
  yield takeLatest(updateExpenseRequest.type, handleUpdateExpense);
  yield takeLatest(cancelExpenseRequest.type, handleCancelExpense);
}
